<template>
  <div class="sales-invoice-detail">
    <b-card v-if="loading">
      <div class="text-center py-5">
        <b-spinner variant="primary" />
        <p class="mt-2 text-muted">
          Chargement de la facture...
        </p>
      </div>
    </b-card>

    <template v-else-if="invoice">
      <!-- Invoice Header -->
      <b-card class="mb-3">
        <div class="d-flex justify-content-between align-items-start">
          <div>
            <h3 class="mb-1">
              Facture {{ invoice.invoice_number }}
            </h3>
            <p class="text-muted mb-0">
              Créée le {{ formatDate(invoice.created_at) }}
            </p>
          </div>
          <b-badge
            :variant="invoice.payment_status === 'paid' ? 'success' : 'warning'"
            class="px-3 py-2"
          >
            {{ invoice.payment_status_display }}
          </b-badge>
        </div>
      </b-card>

      <!-- Invoice Details -->
      <b-row>
        <b-col
          md="6"
          class="mb-3"
        >
          <b-card>
            <h5 class="mb-3">
              <feather-icon
                icon="CalendarIcon"
                size="18"
                class="mr-1"
              />
              Période de Commission
            </h5>
            <div class="detail-row">
              <span class="label">Date de début</span>
              <span class="value">{{ formatDate(invoice.period_start) }}</span>
            </div>
            <div class="detail-row">
              <span class="label">Date de fin</span>
              <span class="value">{{ formatDate(invoice.period_end) }}</span>
            </div>
            <div class="detail-row">
              <span class="label">Taux de commission</span>
              <span class="value">{{ formatAmount(invoice.commission_rate) }} DT / commande</span>
            </div>
          </b-card>
        </b-col>

        <b-col
          md="6"
          class="mb-3"
        >
          <b-card>
            <h5 class="mb-3">
              <feather-icon
                icon="DollarSignIcon"
                size="18"
                class="mr-1"
              />
              Résumé
            </h5>
            <div class="detail-row">
              <span class="label">Nombre de commandes</span>
              <span class="value">{{ invoice.total_orders }}</span>
            </div>
            <div class="detail-row">
              <span class="label">Montant total</span>
              <span class="value text-success font-weight-bold">
                {{ formatAmount(invoice.total_amount) }} DT
              </span>
            </div>
            <div
              v-if="invoice.payment_date"
              class="detail-row"
            >
              <span class="label">Date de paiement</span>
              <span class="value">{{ formatDate(invoice.payment_date) }}</span>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Order Items -->
      <b-card>
        <h5 class="mb-3">
          <feather-icon
            icon="PackageIcon"
            size="18"
            class="mr-1"
          />
          Commandes incluses ({{ invoice.items ? invoice.items.length : 0 }})
        </h5>

        <b-table
          v-if="invoice.items && invoice.items.length > 0"
          :items="invoice.items"
          :fields="itemFields"
          responsive
          hover
          small
        >
          <template #cell(commission_amount)="data">
            <span class="text-success">
              {{ formatAmount(data.item.commission_amount) }} DT
            </span>
          </template>
        </b-table>

        <div
          v-else
          class="text-center py-4 text-muted"
        >
          <feather-icon
            icon="InboxIcon"
            size="32"
          />
          <p class="mt-2">
            Aucune commande dans cette facture
          </p>
        </div>
      </b-card>

      <!-- Back Button -->
      <div class="mt-3">
        <b-button
          variant="outline-secondary"
          :to="{ name: 'sales-invoices' }"
        >
          <feather-icon
            icon="ArrowLeftIcon"
            size="14"
            class="mr-1"
          />
          Retour aux factures
        </b-button>
      </div>
    </template>

    <b-card v-else>
      <div class="text-center py-5">
        <feather-icon
          icon="AlertCircleIcon"
          size="48"
          class="text-danger"
        />
        <p class="mt-2 text-muted">
          Facture non trouvée
        </p>
        <b-button
          variant="primary"
          :to="{ name: 'sales-invoices' }"
        >
          Retour aux factures
        </b-button>
      </div>
    </b-card>
  </div>
</template>

<script>
import axios from 'axios'
import {
  BCard,
  BRow,
  BCol,
  BTable,
  BButton,
  BSpinner,
  BBadge,
} from 'bootstrap-vue'

export default {
  name: 'SalesInvoiceDetail',
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      invoice: null,
      itemFields: [
        { key: 'order_id', label: 'N° Commande' },
        { key: 'order_name', label: 'Nom' },
        { key: 'order_supplier_name', label: 'Fournisseur' },
        { key: 'delivered_date', label: 'Date Livraison', formatter: this.formatDate },
        { key: 'commission_amount', label: 'Commission', class: 'text-right' },
      ],
    }
  },
  mounted() {
    this.loadInvoice()
  },
  methods: {
    async loadInvoice() {
      this.loading = true
      try {
        const response = await axios.get(`/api/commissions/commission-invoices/${this.$route.params.id}/`)
        this.invoice = response.data
      } catch (error) {
        console.error('Error loading invoice:', error)
        this.$bvToast.toast('Erreur lors du chargement de la facture', {
          title: 'Erreur',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.loading = false
      }
    },
    formatAmount(value) {
      if (value === null || value === undefined) return '0.000'
      return parseFloat(value).toFixed(3)
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    },
  },
}
</script>

<style scoped>
.detail-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #eee;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-row .label {
  color: #6c757d;
}

.detail-row .value {
  font-weight: 500;
}
</style>
